import React, { useState, useEffect } from "react";
import { format, parse } from "date-fns";
import MasterTable from "../../../components/common/MasterTable";
import { axiosPostApi } from "../../../helpers/interceptorServices";
import { apiList } from "../../../helpers/apiList";
import CustomAlert from "../../../helpers/CustomAlert";
import Loader from "../../../components/Loader/Loader";
import HeaderWithBreadcrumb from "../../../components/common/HeaderWithBreadcrumb";
import { formatAmount, formatDate } from "../../../helpers/helper";
import PaymentHistoryFilterModal from "./PaymentHistoryFilterModal";
import ReportTypeModal from "./ReportTypeModal";
import DownloadReport from "../../../components/transactionReport/DownloadReport";
import { useSelector } from "react-redux";
import FilterBar from "../../../components/common/FilterBar";
import { asStatusList } from "../../../components/common/filterOptions";

const PAYMENT_STATUS_OPTIONS = [
  { value: "INITIATED", label: "Initiated" },
  { value: "SUCCESS", label: "Success" },
  { value: "FAILED", label: "Failed" },
];

const PAYMENT_METHOD_OPTIONS = [
  { value: "UPI", label: "UPI" },
  { value: "CCI", label: "Credit Card" },
  { value: "DCI", label: "Debit Card" },
];

// Helper function to get date 7 days ago in YYYY-MM-DD format
const getLast7DaysDate = () => {
  const today = new Date();
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 7);

  const year = sevenDaysAgo.getFullYear();
  const month = String(sevenDaysAgo.getMonth() + 1).padStart(2, "0");
  const day = String(sevenDaysAgo.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Helper function to get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const defaultFilterData = {
  status: [],
  fromDate: getLast7DaysDate(),
  toDate: getTodayDate(),
  paymentMethod: "",
  reconStatus: "",
  rrn: "",
  search: "",
};

const PaymentHistoryMaster = () => {
  const pageSize = 10;
  const loggedInUserName = sessionStorage.getItem("username");
  const { user } = useSelector((state) => state.auth);

  const [noData, setNoData] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [modalAction, setModalAction] = useState("");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [pageDetails, setPageDetails] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterData, setFilterData] = useState(defaultFilterData);
  const [isModalOpenFilter, setIsModalOpenFilter] = useState(false);
  const [isOpenDownloadReportModal, setIsOpenDownloadReportModal] =
    useState(false);
  const [loadingDowRep, setLoadingDowRep] = useState(false);
  const [downloadReportData, setDownloadReportData] = useState([]);
  const [pageDowRepDetails, setPageDowRepDetails] = useState(null);
  const [noDowRepData, setNoDowRepData] = useState(false);

  const handleReset = () => {
    setFilterData(defaultFilterData);
    fetchPaymentHistoryData(1, defaultFilterData);
    setCurrentPage(1);
  };

  const openModalFilter = () => {
    setIsModalOpenFilter(true);
  };

  const closeModalFilter = () => {
    setIsModalOpenFilter(false);
  };

  const openModalReportDown = () => {
    setIsOpenDownloadReportModal(true);
  };

  const closeModalReportDown = () => {
    setIsOpenDownloadReportModal(false);
  };

  useEffect(() => {
    fetchPaymentHistoryData(1, filterData);
  }, []);

  const fetchPaymentHistoryData = async (currentPage, filters = filterData) => {
    try {
      const requestBody = {
        username: loggedInUserName,
        ...(filters?.status?.length > 0 && { status: filters.status }),
        ...(filters?.fromDate && { fromDate: formatDate(filters.fromDate) }),
        ...(filters?.toDate && { toDate: formatDate(filters.toDate) }),
        ...(filters?.paymentMethod && { paymentMethod: filters.paymentMethod }),
        ...(filters?.search && { search: filters.search }),
        // ...(filters?.reconStatus && { reconStatus: filters.reconStatus }),
        // ...(filters?.rrn && { rrn: filters.rrn }),
      };

      const response = await axiosPostApi(
        `${apiList.VEGAAH_FETCH_PAYMENT_HISTORY}?&pageSize=${pageSize}&pageNo=${currentPage}`,
        requestBody,
      );

      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        setPageDetails(response?.data?.pageDetails);

        const modifiedData = modifyStructure(response?.data?.data);

        setData(modifiedData);
        if (modifiedData?.length === 0) {
          setNoData(true);
        } else {
          setNoData(false);
        }
      } else {
        setNoData(true);
        setData([]);

        // "02" is just "no matches": the empty table says so
        if (response?.data?.respCode !== "02" && response?.data?.data !== null) {
          CustomAlert({
            title: "No Data",
            text: "No data available for the specified criteria.",
            icon: "info",
            confirmButtonText: "Okay",
          });
        }
      }
    } catch (error) {
      CustomAlert({
        title: "Error",
        text: "An error occurred while fetching the data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    } finally {
      setLoading(false);
    }
  };

  const modifyStructure = (response) => {
    const modified = response?.map((item, index) => {
      const uploadedDateParsed = item?.createdDate
        ? parse(item.createdDate, "dd-MM-yyyy HH:mm:ss", new Date())
        : null;

      return {
        ...item,
        createdDate: uploadedDateParsed
          ? [
              format(uploadedDateParsed, "dd-MM-yyyy"),
              <br key={index} />,
              format(uploadedDateParsed, "HH:mm:ss"),
            ]
          : "",

        amount: (
          <div
            style={{
              textAlign: "center",
              paddingRight: "24px",
              whiteSpace: "nowrap",
            }}
            title={formatAmount(item.amount)}
          >
            {formatAmount(item.amount)}
          </div>
        ),
      };
    });

    return modified;
  };

  const userHeaders = [
    "Order Id",
    "LMBP Name",
    "Created Date",
    "Transaction Id",
    "Status",
    "Pay Method",
    "Amount (₹)",
    "Email Id",
    "Mobile No.",
    "RRN",
  ];

  const userHeaderToKeyMap = {
    "Order Id": "orderId",
    "LMBP Name": "lmbpName",
    "Created Date": "createdDate",
    "Transaction Id": "transactionId",
    Status: "status",
    "Pay Method": "paymentMethod",
    "Amount (₹)": "amount",
    "Email Id": "email",
    "Mobile No.": "mobile",
    RRN: "rrn",
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditData({});
    setModalAction("");
    setShowReportModal(false);
  };

  const handleDownloadReport = async (currentPage) => {
    setLoadingDowRep(true);
    setNoDowRepData(false);

    try {
      const payload = {
        username: user?.data?.username,
      };

      const response = await axiosPostApi(
        `${apiList.PAYMENT_REPORT_DOWNLOAD_HISTORY}?&pageSize=${pageSize}&pageNo=${currentPage}`,
        payload,
      );

      if (response && response.data) {
        if (response.data.status === true && response.data.respCode === "00") {
          const downloadHistory = response.data.data;
          setPageDowRepDetails(response.data.pageDetails);
          setDownloadReportData(downloadHistory || []);

          if (!downloadHistory || downloadHistory.length === 0) {
            setNoDowRepData(true);
          } else {
            setNoDowRepData(false);
          }
        } else {
          setDownloadReportData([]);
          setNoDowRepData(true);

          CustomAlert({
            title: "Failed",
            text: response.data.message || "Failed to fetch data",
            icon: "error",
            confirmButtonText: "Retry",
          });
        }
      } else {
        setDownloadReportData([]);
        setNoDowRepData(true);
      }
    } catch (error) {
      console.error("Error fetching download history:", error);
      setDownloadReportData([]);
      setNoDowRepData(true);

      CustomAlert({
        title: "Error",
        text: "An error occurred while fetching the data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    } finally {
      setLoadingDowRep(false);
    }
  };

  const handleUser = (data, actionType = "view") => {
    setModalAction(actionType);
    if (actionType === "add") {
      setEditData({});
      setShowModal(true);
    } else if (actionType === "report") {
      setShowReportModal(true);
    } else {
      let smsDataL = data?.find((item) => item?.id === data?.id);
      if (smsDataL) {
        setEditData(smsDataL);
        setShowModal(true);
      }
    }
  };

  const DownloadHeaders = [
    "Report Type",
    "Req Date & Time",
    "From Date",
    "To Date",
    "Status",
    "File",
  ];

  const DownloadHeaderToKeyMap = {
    "Report Type": "reportType",
    "Req Date & Time": "createdDate",
    "From Date": "reqFromDate",
    "To Date": "reqToDate",
    Status: "status",
    File: "fileUrl",
  };

  const pageSizeCount = pageDetails === null ? 0 : pageDetails.pageSize;
  const totalRecord = pageDetails === null ? 0 : pageDetails.totalRecords;

  return (
    <>
      <HeaderWithBreadcrumb title="Payment History" breadcrumb="Master" />
      <FilterBar
        values={filterData}
        onChange={(next) => {
          setFilterData(next);
          setCurrentPage(1);
          fetchPaymentHistoryData(1, next);
        }}
        search={{ key: "search", placeholder: "Search order / transaction ID, RRN, LMBP, email or mobile" }}
        selects={[
          { key: "status", allLabel: "All statuses", chipLabel: "Status", options: PAYMENT_STATUS_OPTIONS, ...asStatusList },
          { key: "paymentMethod", allLabel: "All methods", chipLabel: "Method", options: PAYMENT_METHOD_OPTIONS },
        ]}
        dateRange={{ fromKey: "fromDate", toKey: "toDate", label: "Date" }}
      />
      <div>
        <div className="bg-[#FFFFFF] shadow-custom rounded-[14px] px-[10px] py-[10px]">
          {loading ? (
            <div className="flex justify-center items-center py-10">
              <Loader />
            </div>
          ) : (
            <>
              <MasterTable
                loading={loading}
                pageDetails={pageDetails}
                headers={userHeaders}
                data={data}
                handleAction={handleUser}
                filterFields={["orderId"]}
                entityType="Payment History"
                headerToKeyMap={userHeaderToKeyMap}
                noData={noData}
                currentPage={currentPage}
                pageSize={pageSizeCount}
                totalRecord={totalRecord}
                fetchNextData={(page) =>
                  fetchPaymentHistoryData(page, filterData)
                }
                setCurrentPage={setCurrentPage}
                openModalFilter={openModalFilter}
                handleReset={handleReset}
                filterData={filterData}
                fetchData={() => {}}
                openModalSmsDown={openModalReportDown}
                hideFilterControls
              />
              {isModalOpenFilter ? (
                <PaymentHistoryFilterModal
                  show={isModalOpenFilter}
                  handleClose={closeModalFilter}
                  setSmsData={setData}
                  setPageDetails={setPageDetails}
                  setNoData={setNoData}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  filterData={filterData}
                  setFilterData={setFilterData}
                  handleReset={handleReset}
                />
              ) : null}
              {showReportModal && (
                <ReportTypeModal
                  show={showReportModal}
                  handleClose={handleCloseModal}
                />
              )}

              {isOpenDownloadReportModal && (
                <DownloadReport
                  handleopenReportDown={isOpenDownloadReportModal}
                  handleCloseReportDown={closeModalReportDown}
                  callApi={handleDownloadReport}
                  data={downloadReportData}
                  pageDowRepDetails={pageDowRepDetails}
                  noData={noDowRepData}
                  loading={loadingDowRep}
                  header={DownloadHeaders}
                  headerToKey={DownloadHeaderToKeyMap}
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default PaymentHistoryMaster;
